import React, { useRef } from "react";
import { useState } from "react";
import { FiSearch, FiPlus, FiRefreshCcw } from "react-icons/fi";
import interviewApi, { InterviewTags, useSearchJobsForInterviewQuery } from "../redux/api/interview.api";
import { useSearchParams } from "react-router-dom";
import { LocalStorage } from "../types/local_storage.enum";
import { JobForInterview } from "../types/interview.v2.type";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { MdRefresh } from "react-icons/md";
import { toast } from "react-toastify";


//  Debounced search functionality
const useDebouncedSearch = (initialValue: string, delay: number = 300) => {
    const [search, setSearch] = useState(initialValue);
    const [debouncedSearch, setDebouncedSearch] = useState(initialValue);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [search, delay]);

    return { search, debouncedSearch, setSearch };
}

interface PositionListProps {
    setNewPosition: (open: boolean) => void;
    selectedJobId: number | null;
    setSelectedJobId: (id: number | null) => void;
    jobSelectionError: boolean;
    setJobSelectionError: (error: boolean) => void;
    setNoJobsFound: (noJob: boolean) => void;
}

export default function PositionList({ setNewPosition, selectedJobId, setSelectedJobId, jobSelectionError, setJobSelectionError, setNoJobsFound }: PositionListProps) {
    const [search, setSearch] = useState("")
    const user = useAppSelector(state => state.auth.user)
    const dispatch = useAppDispatch();
    const [searchTerm, setSearchTerm] = useState("")
    const [searchParams, setSearchParams] = useSearchParams();
    const localData = localStorage.getItem(LocalStorage.LOCAL_INTERVIEWD_LIST)
    const [refreshing, setRefreshing] = useState(false);
    const { data, isFetching } = useSearchJobsForInterviewQuery({ searchKey: search, userId: user?.primaryEmail || "" }, {
        refetchOnMountOrArgChange: true,
        skip: (!!localData && !search.trim() && !refreshing)
    });
    const toggleSelect = (id: number) => {
        setSelectedJobId(selectedJobId === id ? null : id);
        setJobSelectionError(id !== null && id !== selectedJobId ? false : jobSelectionError);
    };

    const filteredJobs = React.useMemo(() => {
        let jobs: JobForInterview[] = []
        if (localData && !refreshing && !search.trim()) {
            jobs = JSON.parse(localData)
            return jobs.sort((a, b) => new Date(b.lastUpdatedDate).getTime() - new Date(a.lastUpdatedDate).getTime()); // Descending order (latest first)
        }
        jobs = data?.jobs ? [...data.jobs] : [];
        if (!search.trim() && jobs.length) {
            setRefreshing(false);
            localStorage.setItem(LocalStorage.LOCAL_INTERVIEWD_LIST, JSON.stringify(jobs))
        }
        return jobs.sort((a, b) => new Date(b.lastUpdatedDate).getTime() - new Date(a.lastUpdatedDate).getTime()); // Descending order (latest first)
    }, [data?.jobs, search, localData]);

    React.useEffect(() => {
        if (!isFetching && filteredJobs.length === 0) {
            if (search.trim() !== "") {
                setNoJobsFound(true);
                setNewPosition(false);
            } else {
                setNoJobsFound(false);
                setNewPosition(true);
            }
        } else {
            setNewPosition(false);
            setNoJobsFound(false);
        }

    }, [filteredJobs, search, isFetching]);

    const listContainerRef = useRef<HTMLDivElement>(null);
    const selectedJobRef = useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (selectedJobId && selectedJobRef.current && listContainerRef.current) {
            listContainerRef.current.scrollTo({
                top:
                    selectedJobRef.current.offsetTop -
                    listContainerRef.current.offsetTop,
                behavior: "smooth",
            });
        }
    }, [selectedJobId]);

    React.useEffect(() => {
        if (!searchTerm.trim()) {
            setSearch('')

        }
    }, [searchTerm])

    return (
        <div className="h-[20rem] tablet:h-auto w-full">
            {

                <div className="h-full dark:text-gray-100 text-gray-600 rounded-xl space-y-2 transition-colors duration-300">
                    {/* Top bar */}
                    <div className="flex items-center gap-1">
                        {/* Search */}
                        <div className="flex relative  items-stretch flex-1 py-1 rounded-lg pr-2">
                            <input
                                type="text"
                                placeholder="Search position"
                                value={searchTerm}
                                style={{
                                    // border: "none",
                                    outline: "none"
                                }}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="bg-transparent border-transparent border-none hover:border-accent-500 outline-none px-3 py-2 w-full placeholder-gray-500 dark:placeholder-gray-400"
                            />
                            <button
                                className=" bg-accent-500 hover:accent-accent-600 my-[1px] mb-2  px-3 absolute right-0 -bottom-[4px] py-2 rounded-r-md  flex items-center justify-center hover:shadow-md shadow-sm "
                                onClick={(e) => {
                                    e.preventDefault()
                                    setSearch(searchTerm)
                                }}
                            >

                                <FiSearch className="text-white text-lg " />
                            </button>
                        </div>
                        <button
                            className="flex items-center gap-1 bg-accent-500 hover:bg-accent-600 text-white font-medium px-4 py-[8px] rounded-lg transition"
                            onClick={(e) => {
                                e.preventDefault()
                                setRefreshing(true)
                                dispatch(interviewApi.util.invalidateTags([InterviewTags.SEARCH_JOBS_FOR_INTERVIEW]))
                            }}
                        >

                            <FiRefreshCcw className="text-white text-lg  bg" />
                        </button>

                        {/* New Button */}
                        <button
                            className="flex items-center gap-1 bg-accent-500 hover:bg-accent-600 text-white font-medium px-4 py-[9px] rounded-lg transition"
                            onClick={(e) => {
                                e.preventDefault()
                                const params = new URLSearchParams(searchParams)
                                params.delete('jobId')
                                setSearchParams(params)
                                setSelectedJobId(null)
                                setNewPosition(true)
                            }}
                        >
                            <FiPlus /> New
                        </button>
                    </div>

                    {/* Job cards */}

                    {
                        isFetching ? (
                            <div className="flex items-center justify-center w-full !h-[15rem] mobile:!h-[17rem] tablet:!h-[20rem] ">
                                <div className="w-12 h-12 border-4 border-t-transparent border-accent-500 rounded-full animate-spin"></div>
                            </div>
                        ) : filteredJobs.length === 0 ? (
                            <div className="text-center h-[15rem] flex items-center justify-center text-gray-500 dark:text-gray-400">
                                No positions found
                            </div>
                        ) :
                            <div ref={listContainerRef} className="space-y-3 custom-scrollbar overflow-y-auto !h-auto  !max-h-[15rem] mobile:!max-h-[17rem] tablet:!max-h-[20rem]">
                                {filteredJobs.map((job) => (
                                    <div

                                        key={job.jobId}
                                        {
                                        ...(
                                            job.jobId === selectedJobId ? { ref: selectedJobRef } : {}
                                        )
                                        }
                                        className={`flex items-start gap-3 border dark:border-gray-700 rounded-lg p-3 shadow-sm transition-colors duration-300 ${selectedJobId === job.jobId
                                            ? "bg-accent-100 dark:bg-[#212121] !border !border-accent-500"
                                            : "bg-white dark:bg-[#232226]"
                                            }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedJobId === job.jobId}
                                            onChange={() => toggleSelect(job.jobId)}
                                            className="mt-1 w-5 h-5  accent-accent-500 cursor-pointer"
                                        />

                                        <div className="flex flex-row justify-between w-[90%]">
                                            <div className="flex flex-col gap-1 max-w-[54%]">
                                                <div className="text-[8px] overflow-hidden whitespace-nowrap  inline-block max-w-full  md:text-[10px] text-ellipsis  lg:text-sm">
                                                    <div className="font-semibold text-ellipsis">{job.jobTitle || "N/A"} </div>
                                                    Role: <span className="font-medium">{job.jobRole || "N/A"}</span>
                                                </div>
                                            </div>
                                            <div className="flex flex-col overflow-hidden whitespace-nowrap   text-ellipsis gap-1 max-w-[45%]">

                                                <div className="text-[8px] md:text-[10px] lg:text-sm">
                                                    Company: <span className="font-medium">{job.companyName || "N/A"}</span>
                                                </div>
                                                <div className="text-[8px] md:text-[10px] lg:text-sm text-gray-500 dark:text-gray-400">
                                                    Date: {job.lastUpdatedDate ? new Date(job.lastUpdatedDate).toLocaleDateString() : "N/A"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                            </div>

                    }
                    {jobSelectionError && (
                        <div className="text-red-500 text-sm -mt-3">
                            Please select a position to proceed.
                        </div>
                    )}
                </div>
            }
        </div>
    );
}
